import { Footer } from '#app/components/footer.tsx'
import { Header } from '#app/components/header.tsx'

export function meta() {
	return [
		{ title: 'Privacy Policy - Task Sentry' },
		{
			name: 'description',
			content:
				'Privacy Policy for Task Sentry - Learn how we collect, use, and protect your information when you use our AI-powered focus protection service.',
		},
	]
}

export default function PrivacyPolicy() {
	return (
		<div className="min-h-screen bg-white">
			<Header />
			<div className="mx-auto max-w-4xl px-4 py-12">
				<div className="prose prose-lg max-w-none">
					<h1 className="mb-8 text-4xl font-bold text-gray-900">
						Privacy Policy for Task Sentry
					</h1>
					<p className="mb-8 text-gray-600">Last Updated: July 7, 2025</p>

					<p className="mb-6">
						Thank you for using Task Sentry ("we," "us," or "our"). This Privacy
						Policy explains how we collect, use, disclose, and safeguard your
						information when you use our Chrome extension, Task Sentry (the
						"Service").
					</p>

					<p className="mb-8">
						Please read this Privacy Policy carefully. By using the Service, you
						agree to the collection and use of information in accordance with
						this policy.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						1. Information We Collect
					</h2>
					<p className="mb-4">
						We collect several different types of information for various
						purposes to provide and improve our Service to you.
					</p>

					<h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
						A. Information You Provide to Us Directly:
					</h3>

					<p className="mb-4">
						<strong>Account and Authentication Information:</strong> When you
						create a Task Sentry account, we use Supabase for authentication. We
						may collect personally identifiable information such as your name
						and email address.
					</p>

					<p className="mb-4">
						<strong>Payment and Financial Information:</strong> If you subscribe
						to a paid version of Task Sentry, our third-party payment processor,
						Stripe, will collect financial information required to process the
						transaction. This can include your name, billing address, and credit
						card information. We do not store your full credit card details on
						our servers.
					</p>

					<p className="mb-4">
						<strong>Task Information:</strong> To provide the core functionality
						of the Service, you provide your specific goal or objective for a
						work session (e.g., "Write a marketing brief").
					</p>

					<h3 className="mt-6 mb-3 text-xl font-semibold text-gray-900">
						B. Information We Collect Automatically (Usage Data):
					</h3>

					<p className="mb-4">
						<strong>Website Content, Web History, and Visual Data:</strong> The
						core function of Task Sentry is to analyze your active tab to
						determine if it is relevant to your stated task. This involves
						programmatically accessing the URL, title, and text content of the
						web pages you visit, as well as capturing a temporary,
						low-resolution screenshot of the visible page. This combination of
						textual and visual data is essential for our on-device AI to perform
						an accurate analysis. <strong>Important:</strong> All AI analysis is
						performed locally on your device using Chrome's built-in Prompt API.
						Your browsing content and screenshots are never sent to our servers
						for processing.
					</p>

					<p className="mb-4">
						<strong>User Activity:</strong> The Service monitors your browser
						activity, such as when you switch tabs or when a page has finished
						loading. This is necessary to know when to trigger a page analysis.
					</p>

					<p className="mb-4">
						<strong>Location Information:</strong> When our Service communicates
						with our backend servers (hosted on Supabase) or third-party
						services (Stripe), your IP address is inherently processed. An IP
						address can be used to estimate your general geographical location.
					</p>

					<p className="mb-4">
						<strong>Usage and Analytics Data:</strong> We use a third-party
						analytics service, PostHog, to collect information about how you
						interact with our Service. This includes events like features used
						or buttons clicked. To help us improve the product and provide
						better support, this usage data is associated with your user account
						ID.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						2. How We Use Your Information
					</h2>
					<p className="mb-4">
						We use the collected data for the following purposes:
					</p>

					<ul className="mb-4 list-disc space-y-2 pl-6">
						<li>
							<strong>To Provide and Maintain the Service:</strong> To perform
							the core function of analyzing your active tab against your stated
							goal and blocking distractions. This analysis is performed
							entirely on your device using Chrome's Prompt API and does not
							involve sending your browsing data to external servers.
						</li>
						<li>
							<strong>To Manage Your Account:</strong> To create and manage your
							account and handle authentication via Supabase.
						</li>
						<li>
							<strong>To Process Payments:</strong> To manage subscriptions and
							process payments securely through Stripe.
						</li>
						<li>
							<strong>To Store Your Data:</strong> To store your settings,
							tasks, and related data on our backend, powered by Supabase.
						</li>
						<li>
							<strong>To Communicate With You:</strong> To contact you with
							updates, security alerts, or administrative messages.
						</li>
						<li>
							<strong>To Improve Our Service:</strong> To understand how users
							interact with Task Sentry and to improve its functionality and
							user experience.
						</li>
					</ul>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						3. Local AI Processing
					</h2>
					<p className="mb-4">
						Task Sentry uses advanced AI technology to analyze web content and
						determine relevance to your tasks. We prioritize your privacy by
						utilizing Chrome's built-in Prompt API, which enables:
					</p>

					<ul className="mb-4 list-disc space-y-2 pl-6">
						<li>
							<strong>On-Device Processing:</strong> All AI analysis happens
							locally on your device. Your browsing content never leaves your
							computer.
						</li>
						<li>
							<strong>No External AI Services:</strong> We do not use
							third-party AI services like OpenAI, Google AI, or similar
							platforms for content analysis.
						</li>
						<li>
							<strong>Privacy by Design:</strong> Chrome's Prompt API ensures
							your sensitive browsing data remains private and secure on your
							device.
						</li>
						<li>
							<strong>Real-Time Analysis:</strong> Local processing enables
							instant analysis without network delays or data transmission.
						</li>
					</ul>

					<p className="mb-4">
						This approach means that while we may store your task descriptions
						and settings in our database, the actual analysis of your browsing
						content is performed entirely offline using your device's local AI
						capabilities.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						4. How We Share and Disclose Your Information
					</h2>
					<p className="mb-4">
						We do not sell your personal data. We may share your information
						with third-party service providers that we use to operate our
						Service:
					</p>

					<ul className="mb-4 list-disc space-y-2 pl-6">
						<li>
							<strong>Stripe:</strong> For secure payment processing. We
							recommend you review Stripe's privacy policy.
						</li>
						<li>
							<strong>Supabase:</strong> For backend database hosting, data
							storage, and user authentication. We recommend you review
							Supabase's privacy policy.
						</li>
						<li>
							<strong>PostHog:</strong> For product analytics to help us
							understand user engagement and improve the Service. We recommend
							you review PostHog's privacy policy.
						</li>
					</ul>

					<p className="mb-4">
						We may also disclose your information if required to do so by law or
						in the good faith belief that such action is necessary to comply
						with a legal obligation, protect our rights, or protect the personal
						safety of users or the public.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						5. Data Storage and Security
					</h2>
					<p className="mb-4">
						Your data is stored on servers provided by Supabase. We take
						commercially reasonable measures to protect your information from
						unauthorized access, use, or disclosure. However, please be aware
						that no method of transmission over the Internet or method of
						electronic storage is 100% secure.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						6. Data Retention
					</h2>
					<p className="mb-4">
						We will retain your personal information only for as long as is
						necessary for the purposes set out in this Privacy Policy, such as
						maintaining your account. We will retain and use your information to
						the extent necessary to comply with our legal obligations, resolve
						disputes, and enforce our agreements.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						7. Your Rights and Choices
					</h2>
					<p className="mb-4">
						You have certain rights regarding the personal information we hold
						about you. Depending on your location, these may include the right
						to:
					</p>

					<ul className="mb-4 list-disc space-y-2 pl-6">
						<li>Access, update, or delete the information we have on you.</li>
						<li>Request that we stop processing your personal information.</li>
					</ul>

					<p className="mb-4">
						You can manage your account information through your account
						settings. To request data deletion, please contact us at the email
						below. You can stop all data collection by the extension by
						uninstalling it from your Chrome browser.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						8. Children's Privacy
					</h2>
					<p className="mb-4">
						Our Service is not intended for use by anyone under the age of 13.
						We do not knowingly collect personally identifiable information from
						children under 13.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						9. Changes to This Privacy Policy
					</h2>
					<p className="mb-4">
						We may update our Privacy Policy from time to time. We will notify
						you of any changes by posting the new Privacy Policy on this page
						and updating the "Last Updated" date.
					</p>

					<h2 className="mt-8 mb-4 text-2xl font-semibold text-gray-900">
						10. Contact Us
					</h2>
					<p className="mb-4">
						If you have any questions about this Privacy Policy, please contact
						us at:
					</p>
					<p className="mb-4">
						<strong>Email:</strong>{' '}
						<a
							href="mailto:support@tasksentry.com"
							className="text-blue-600 hover:text-blue-800"
						>
							support@tasksentry.com
						</a>
					</p>
				</div>
			</div>
			<Footer />
		</div>
	)
}
